/**
 * Per-viewer dismissal of maintenance banners.
 *
 * `sessionStorage`, not `localStorage` and not the database: a maintenance
 * notice is worth seeing once per sitting, and a reader who closes the tab and
 * comes back an hour later should be told again that the window is still open.
 * Nothing is worth a write to Convex, and the portal reader has no account to
 * write it against.
 */
const DISMISSED_KEY = 'bill-alps:maintenance-dismissed';

/** Ids the viewer has dismissed in this browser session; `[]` when unreadable. */
export function getDismissedBanners(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Anything else — a hand-edited value, a key collision, an older shape —
    // means "nothing dismissed" rather than a crashed banner.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** Idempotent: dismissing twice leaves one entry. */
export function dismissBanner(id: string): string[] {
  const next = getDismissedBanners();
  if (!next.includes(id)) next.push(id);
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private-mode quota or a blocked storage: the banner still hides for
      // the rest of this page's life via component state.
    }
  }
  return next;
}
